import { actions, actionTypes, selectors } from 'data'
import { ReadPriceActionType } from 'data/actions'
import { notifyError } from 'data/App/sagas'
import { parseSymbol, SymbolType } from 'data/Instrument/types'
// eslint-disable-next-line import/no-duplicates
import subDays from 'date-fns/sub_days'
// eslint-disable-next-line import/no-duplicates
import subHours from 'date-fns/sub_hours'
// eslint-disable-next-line import/no-duplicates
import subMinutes from 'date-fns/sub_minutes'
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { getAll } from 'services/api/price'
import webSocket from 'services/api/webSocket'
import { logger, priceHelper, sanitizeMessage } from 'utils'
import { Response } from 'utils/fetchApi'

import {
  PriceMessageType,
  PriceRangeType,
  PriceRangeValueType,
  PriceResponseType,
  PriceType,
  PriceUpdateType
} from './types'

const messageManager = function* (action) {
  const message: PriceMessageType = action.payload

  const symbol = yield select(selectors.instrument.getSymbol)
  if (message.event === webSocket.event.REJECTED) {
    logger.warn(JSON.stringify(message))
    return
  }

  if (message.event === webSocket.event.UPDATED) {
    const data = sanitizeMessage(message) as PriceUpdateType
    const price = priceHelper.fromArray(data.price)

    // Horrible hack to notify trading view we got updates
    const liveCallback = yield select(selectors.price.getLiveCallback)
    if (liveCallback) {
      liveCallback(price)
    }

    // EFN-673, EFN-567
    // We submit updates only if currently selected pair matches update pair
    if (symbol === data.symbol) {
      yield put(actions.price.setUpdate(price))
    }
    return
  }

  if (message.event === webSocket.event.SUBSCRIBED) {
    yield put(actions.price.setSubscribed())
  }
}

const periods = 400

const subRange = (date: Date, range: PriceRangeValueType): Date => {
  switch (range) {
    case '5m':
      return subMinutes(date, 5 * periods)
    case '15m':
      return subMinutes(date, 15 * periods)
    case '1h':
      return subHours(date, 1 * periods)
    case '6h':
      return subHours(date, 6 * periods)
    case '1d':
      return subDays(date, 1 * periods)
    case '1m':
    default:
      return subMinutes(date, 1 * periods)
  }
}

const read = function* (from: Date, to: Date, resolution: number) {
  const symbol: SymbolType = yield select(selectors.instrument.getSymbol)
  const { base, counter } = parseSymbol(symbol)

  const data: Response<PriceResponseType> = yield call(
    getAll,
    base,
    counter,
    from.getTime(),
    to.getTime(),
    resolution
  )

  if (!data.ok) {
    yield call(notifyError, data.error.cause, 'KO_GENERIC')
    return []
  }
  const prices = data.response.prices.map(priceHelper.fromArray) as PriceType[]
  yield put(actions.price.setAll(prices))
  return prices
}

// Used by our charts
const readByRange = function* () {
  const range: PriceRangeType = yield select(selectors.price.getCurrentRange)
  const to = new Date()
  const from = subRange(to, range.value)

  yield call(read, from, to, range.granularity)
}

// Used by tradingview
const readByDate = function* (action: ReadPriceActionType) {
  const { callback, from, resolution, to } = action.payload
  const prices = yield call(read, new Date(from), new Date(to), resolution)
  callback(prices, { noData: prices.length === 0 })

  // Dirty little hack to update redux state when trading view changes date range
  // This changes the state, which makes a react component change and resubscribe
  // to WS updates
  const currentRange: PriceRangeType = yield select(selectors.price.getCurrentRange)
  if (currentRange.granularity !== resolution) {
    const ranges: PriceRangeType[] = yield select(selectors.price.getRanges)
    const newRange = ranges.find((range) => range.granularity === resolution)
    if (newRange) {
      yield put(actions.price.setRange(currentRange.value, newRange.value))
    }
  }
}

export default function* priceMain() {
  yield takeLatest(actionTypes.price.SET_RANGE, readByRange)
  yield takeLatest(actionTypes.price.READ, readByRange)
  yield takeEvery(actionTypes.price.READ_BY_DATE, readByDate)
  const channel = webSocket.channel.PRICES
  yield takeEvery(
    (action) => action.type === actionTypes.socket.RECEIVED && action.payload.channel === channel,
    messageManager
  )
}
